import { oc } from "@orpc/contract";
import { z } from "zod";
import { CustomerEmail, PersonName, PhoneNumber, TurnstileToken } from "./customer-account";
import { AddressFields, withPostcode } from "./customer-address";
import { Cents } from "./money";
import { PaymentMethod, ShopOrder } from "./order";
import { PaymentToken } from "./payment";

/**
 * Checkout (spec, "Mand en checkout"; #89): turns the cart into an order, for a guest or a
 * signed-in customer. In one atomic write the order is made with a snapshot of the addresses, the
 * lines (price, VAT per line), shipping, discount and totals, it gets the next order number, the
 * stock is reserved (#90: 30 minutes, 7 days for a bank transfer), the discount code's use is counted and the cart is
 * emptied. Either all of it happens or none of it.
 *
 * - A guest gives email address, first and last name (phone optional), types the addresses, and
 *   passes Turnstile (`turnstileToken`). They become a customer, without an account.
 * - A signed-in customer (the customer session cookie) may leave out the contact details: their
 *   own are used. They pick addresses from their address book (`saved`) or type new ones; a typed
 *   address is copied into the order only, not saved.
 * - The amounts are the cart's, worked out again at this moment: `expectedToPay` is what the shop
 *   showed, and when it differs (a price changed) nothing is placed (`CheckoutCartChanged`).
 * - The shipping address's country must be the cart's delivery country (`PATCH /shop/cart`), since
 *   VAT and shipping were worked out for it.
 * - Rate limited per IP (429 `RateLimited`).
 *
 * The answer is the order and how to pay it (`CheckoutPayment`): checkout starts the Pay.nl payment
 * (#92) right after placing the order. When Pay.nl cannot start it, the order is placed all the
 * same and stays open: the shop starts the payment again with `POST /shop/orders/{orderNumber}/payments`
 * (`PaymentContract.start`). An unpaid order expires after 30 minutes (a bank transfer after 7
 * days): its stock and code use are given back, and no mail goes out.
 *
 * On account (`paymentMethod: "onAccount"`, #97): only a signed-in customer with an approved
 * business profile. The order is ready to ship at once (payment status `on_account`), its stock is
 * sold, and it never expires. It gets its invoice and the order confirmation right away, with the
 * due date (the customer's payment term, 14 days by default) and a Pay.nl transfer reference;
 * `payment.kind` is `onAccount`.
 *
 * Errors (`{ _tag, message }`): `CheckoutCartEmpty` (409), `CheckoutCartChanged` (409: the cart
 * changed, has notices, or was ordered already; fetch it again), `CheckoutOutOfStock` (409: `lines`
 * says which variants and how many are available), `CheckoutDiscountCodeUnavailable` (409),
 * `CheckoutCountryMismatch` (409), `CheckoutNotDeliverable` (409: no shipping rule for the delivery country), `CheckoutDetailsMissing` (400: a guest without email or name),
 * `CheckoutOnAccountNotAllowed` (403: on account without an approved business profile),
 * `AddressNotFound` (404), `CheckoutBotCheckFailed` (400), `CheckoutBotCheckUnavailable` (503),
 * `CheckoutBankTransferNotAllowed` (409: a guest's bank transfer above `GUEST_BANK_TRANSFER_MAX`; says `max`),
 * `RateLimited` (429).
 */

/** An address typed at checkout: an address book address, with an optional recipient and company. */
export const CheckoutAddressInput = withPostcode(
	AddressFields.extend({
		/** Who receives it, when not the customer. */
		recipientName: z
			.string()
			.trim()
			.max(200)
			.nullish()
			.transform((value) => (value ? value : null)),
		company: z
			.string()
			.trim()
			.max(200)
			.nullish()
			.transform((value) => (value ? value : null)),
	}),
);

/** Where to ship or bill: a saved address of the signed-in customer, or one typed now. */
export const CheckoutAddress = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("saved"), addressId: z.string().min(1) }),
	z.object({ kind: z.literal("new"), address: CheckoutAddressInput }),
]);
export type CheckoutAddress = z.output<typeof CheckoutAddress>;

/** The billing address: as the shipping address, or its own. */
export const CheckoutBillingAddress = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("sameAsShipping") }),
	...CheckoutAddress.options,
]);
export type CheckoutBillingAddress = z.output<typeof CheckoutBillingAddress>;

export const CheckoutInput = z.object({
	/** Required for a guest; a signed-in customer's own address is used. */
	email: CustomerEmail.optional(),
	/** Required for a guest; a signed-in customer's own name by default. */
	firstName: PersonName.optional(),
	lastName: PersonName.optional(),
	phone: PhoneNumber.nullish(),
	shippingAddress: CheckoutAddress,
	billingAddress: CheckoutBillingAddress,
	paymentMethod: PaymentMethod,
	/** The `toPay` the shop showed; the order is placed only when it is still the amount. */
	expectedToPay: Cents,
	/** Required for a guest. */
	turnstileToken: TurnstileToken.optional(),
});
export type CheckoutInput = z.input<typeof CheckoutInput>;
export type CheckoutRequest = z.output<typeof CheckoutInput>;

/**
 * How to pay the order just placed:
 *
 * - `redirect`: send the customer to `redirectUrl`, Pay.nl's page for the method (iDEAL, Bancontact,
 *   card, PayPal). Pay.nl sends them back to the shop's `/bestelling/{orderNumber}?betaling={paymentId}`.
 * - `bankTransfer`: the customer transfers `amountCents` with `transferReference` on it within 7
 *   days; `redirectUrl` is Pay.nl's page with the account details, when it gave one.
 * - `notStarted`: the order is placed but Pay.nl did not start the payment: `unavailable` (Pay.nl
 *   could not be reached; try again) or `refused` (Pay.nl said no; try another method). Start it
 *   with `POST /shop/orders/{orderNumber}/payments` while the order is open.
 * - `paid`: there was nothing to pay (a 100% discount, shipping included): the order is paid as it
 *   is placed, without Pay.nl, and the confirmation with its €0 invoice follows by mail.
 *
 * `testMode`: started in Pay.nl's test mode, no real money (every Preview).
 */
export const CheckoutPayment = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("redirect"),
		paymentId: z.string(),
		redirectUrl: z.string(),
		testMode: z.boolean(),
	}),
	z.object({
		kind: z.literal("bankTransfer"),
		paymentId: z.string(),
		amountCents: Cents,
		transferReference: z.string().nullable(),
		redirectUrl: z.string().nullable(),
		testMode: z.boolean(),
	}),
	z.object({
		kind: z.literal("notStarted"),
		reason: z.enum(["unavailable", "refused"]),
		message: z.string(),
	}),
	z.object({ kind: z.literal("paid") }),
	/**
	 * On account (#97): pay the invoice by bank transfer before `dueOn` (`YYYY-MM-DD`), with
	 * Pay.nl's `transferReference`; `redirectUrl` is Pay.nl's page with the account details. Both
	 * `null` (and `paymentId` too) when Pay.nl did not start the transfer: the invoice then names
	 * the shop's own account and the invoice number. The order is placed and ships either way.
	 */
	z.object({
		kind: z.literal("onAccount"),
		dueOn: z.string(),
		amountCents: Cents,
		paymentId: z.string().nullable(),
		transferReference: z.string().nullable(),
		redirectUrl: z.string().nullable(),
	}),
]);
export type CheckoutPayment = z.infer<typeof CheckoutPayment>;

export const PlacedOrder = z.object({
	order: ShopOrder,
	payment: CheckoutPayment,
	/**
	 * Proof that this customer placed the order: `POST /shop/orders/{orderNumber}/payments` takes
	 * it to start a payment again (after `notStarted`, or a payment that failed or was cancelled).
	 * The shop keeps it with the order; it does not expire, but the order does.
	 */
	paymentToken: PaymentToken,
});
export type PlacedOrder = z.infer<typeof PlacedOrder>;

/**
 * The most a guest pays by bank transfer, in cents (#218): a bank transfer reserves the stock for
 * 7 days unpaid. Above it a guest pays with an instant method, or signs in.
 */
export const GUEST_BANK_TRANSFER_MAX = 25_000;

export const CheckoutContract = {
	/** Places the order from the cart. */
	place: oc.route({ method: "POST", path: "/shop/checkout" }).input(CheckoutInput).output(PlacedOrder),
};
